import { useEffect, useRef } from 'react';
import { useTranslator } from 'react-google-translate-mj';

// Global cache to persist across re-renders and save API calls
const globalTranslationCache = {};

// Concurrency queue to prevent 429 Too Many Requests
class AsyncQueue {
    constructor(concurrency) {
        this.concurrency = concurrency;
        this.running = 0;
        this.queue = [];
    }
    
    enqueue(task) {
        return new Promise((resolve, reject) => {
            this.queue.push({ task, resolve, reject });
            this.process();
        });
    }
    
    async process() {
        if (this.running >= this.concurrency || this.queue.length === 0) return;
        
        const { task, resolve, reject } = this.queue.shift();
        this.running++;
        
        try {
            const result = await task();
            resolve(result);
        } catch (err) {
            reject(err);
        } finally {
            this.running--;
            this.process();
        }
    }
}

// 5 concurrent requests maximum
const translateQueue = new AsyncQueue(5);

const AutoTranslateDOM = () => {
    const { currentLanguage, defaultLanguage, translate } = useTranslator();
    const observerRef = useRef(null);
    const translatingRef = useRef(false);

    useEffect(() => {
        const isDefault = currentLanguage === defaultLanguage || !currentLanguage;
        let active = true;

        const isNoTranslate = (node) => {
            let current = node;
            while (current) {
                if (current.nodeType === Node.ELEMENT_NODE) {
                    if (current.getAttribute && current.getAttribute('data-no-translate') === 'true') {
                        return true;
                    }
                    if (current.classList && current.classList.contains('no-translate')) {
                        return true;
                    }
                }
                current = current.parentNode;
            }
            return false;
        };

        const translateNode = async (node) => {
            if (isNoTranslate(node)) return;

            if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA')) {
                const placeholder = node.getAttribute('placeholder');
                if (placeholder && placeholder.trim()) {
                    if (node.__originalPlaceholder === undefined) {
                        node.__originalPlaceholder = placeholder;
                    }
                    const originalText = node.__originalPlaceholder;
                    
                    if (isDefault) {
                        if (node.getAttribute('placeholder') !== originalText) {
                            node.setAttribute('placeholder', originalText);
                        }
                        node.__currentTranslationLanguage = defaultLanguage;
                        return;
                    }
                    if (node.__currentTranslationLanguage === currentLanguage) {
                        return;
                    }
                    // Check cache first
                    if (!globalTranslationCache[currentLanguage]) globalTranslationCache[currentLanguage] = {};
                    if (globalTranslationCache[currentLanguage][originalText]) {
                        if (active) {
                            node.__currentTranslationLanguage = currentLanguage;
                            node.setAttribute('placeholder', globalTranslationCache[currentLanguage][originalText]);
                        }
                        return;
                    }

                    try {
                        const translated = await translateQueue.enqueue(() => translate(originalText));
                        globalTranslationCache[currentLanguage][originalText] = translated;
                        if (active) {
                            node.__currentTranslationLanguage = currentLanguage;
                            node.setAttribute('placeholder', translated);
                        }
                    } catch (err) {
                        console.error('Failed to translate placeholder:', originalText, err);
                    }
                }
                return;
            }

            if (node.__originalText === undefined) {
                node.__originalText = node.nodeValue || '';
            }

            const originalText = node.__originalText;
            if (!originalText || !originalText.trim()) return;

            if (isDefault) {
                if (node.nodeValue !== originalText) {
                    node.nodeValue = originalText;
                }
                node.__currentTranslationLanguage = defaultLanguage;
                return;
            }

            if (node.__currentTranslationLanguage === currentLanguage) {
                return;
            }

            // Check cache first
            if (!globalTranslationCache[currentLanguage]) globalTranslationCache[currentLanguage] = {};
            if (globalTranslationCache[currentLanguage][originalText]) {
                if (active) {
                    node.__currentTranslationLanguage = currentLanguage;
                    node.nodeValue = globalTranslationCache[currentLanguage][originalText];
                }
                return;
            }

            try {
                const translated = await translateQueue.enqueue(() => translate(originalText));
                globalTranslationCache[currentLanguage][originalText] = translated;
                if (active) {
                    node.__currentTranslationLanguage = currentLanguage;
                    node.nodeValue = translated;
                }
            } catch (err) {
                console.error('Failed to translate node:', originalText, err);
            }
        };

        const walkAndTranslate = (element) => {
            if (!element || isNoTranslate(element)) return;
            const ignoredTags = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'SELECT', 'OPTION'];
            if (ignoredTags.includes(element.tagName)) return;
            
            if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
                translateNode(element);
                return;
            }

            let child = element.firstChild;
            while (child) {
                if (child.nodeType === Node.TEXT_NODE) {
                    translateNode(child);
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                    walkAndTranslate(child);
                }
                child = child.nextSibling;
            }
        };

        const rootElement = document.getElementById('root') || document.body;
        walkAndTranslate(rootElement);

        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new MutationObserver((mutations) => {
            if (translatingRef.current) return;

            translatingRef.current = true;
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.TEXT_NODE) {
                            translateNode(node);
                        } else if (node.nodeType === Node.ELEMENT_NODE) {
                            walkAndTranslate(node);
                        }
                    });
                } else if (mutation.type === 'characterData') {
                    const node = mutation.target;
                    if (node.__currentTranslationLanguage !== currentLanguage) {
                        node.__originalText = node.nodeValue || '';
                        node.__currentTranslationLanguage = undefined;
                        translateNode(node);
                    }
                }
            });
            translatingRef.current = false;
        });

        observerRef.current.observe(rootElement, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        return () => {
            active = false;
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [currentLanguage, defaultLanguage, translate]);

    return null;
};

export default AutoTranslateDOM;
